import {
    mapFormDataToTrein,
    Opstartplan,
    Treinnummer,
    Treinserie,
    TreinserieFormData,
    useDeleteOpstartplan,
    usePutOpstartplan,
} from "@/entities/opstartplan";
import {
    Button,
    ConfirmDialog,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    dismissToast,
    Empty,
    EmptyDescription,
    EmptyHeader,
    EmptyImage,
    ErrorDisplay,
    Inputfield,
    toastWithUndo,
} from "@/shared/components";
import { Dispatch, SetStateAction, useState } from "react";
import { Treinseries } from "./opstartplan-details";
import { TreinserieForm } from "./treinserie-form";

type NewEditOpstartplanDialogProps = {
    dossierId: string;
    opstartplan: Opstartplan | null;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
};

export function NewEditOpstartplanDialog({ dossierId, open, onOpenChange, opstartplan }: NewEditOpstartplanDialogProps) {
    const [treinseries, setTreinseries] = useState<Treinserie[]>(opstartplan?.treinseries ?? []);
    const [toelichting, setToelichting] = useState(opstartplan?.toelichting ?? "");
    const [showNewTrainForm, setShowNewTrainForm] = useState(false);
    const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);
    const putOpstartplan = usePutOpstartplan();
    const deleteOpstartplan = useDeleteOpstartplan();

    const updateTreinseries = (serie: number, updateFn: (treinnummers: Treinnummer[]) => Treinnummer[]) => {
        setTreinseries((treinseries) => {
            const serieExists = treinseries.some((ts) => ts.serie === serie);
            const newTreinseries = !serieExists
                ? [...treinseries, { serie, treinnummers: updateFn([]) }]
                : treinseries.map((ts) => (ts.serie === serie ? { ...ts, treinnummers: updateFn(ts.treinnummers) } : ts));

            return newTreinseries.filter((ts) => ts.treinnummers.length > 0).sort(Treinserie.sort);
        });
    };

    function addTreinnummer(trein: Treinnummer, serie?: number) {
        if (!serie) return;
        updateTreinseries(serie, (treinnummers) => [...treinnummers, trein].sort(Treinnummer.sort));
        setShowNewTrainForm(false);
    }

    function handleAddTrein(data: TreinserieFormData, serie?: number) {
        if (!serie) return;
        const trein: Treinnummer = mapFormDataToTrein(data);

        updateTreinseries(serie, (treinnummers) => [...treinnummers, trein].sort(Treinnummer.sort));
        setShowNewTrainForm(false);
    }

    const handleEditTrein = (data: TreinserieFormData, serie: number) => {
        const trein = mapFormDataToTrein(data);
        updateTreinseries(serie, (treinnummers) => treinnummers.map((tn) => (tn.nummer === Number(data.nummer) ? trein : tn)));
    };

    const getTreinnummer = (serie: number, nummer: number) => {
        return (
            treinseries
                .flatMap((treinserie) => (treinserie.serie === serie ? treinserie.treinnummers : null))
                .find((treinnummer) => treinnummer?.nummer === nummer) ?? null
        );
    };

    const handleDeleteTrein = (serie: number, nummer: number) => {
        const existingTreinnummer = getTreinnummer(serie, nummer);
        updateTreinseries(serie, (treinnummers) => treinnummers.filter((tn) => tn.nummer !== nummer));

        const toastId = toastWithUndo(`Trein ${nummer} verwijderd`, undefined, () => {
            if (existingTreinnummer) {
                addTreinnummer(existingTreinnummer, serie);
                dismissToast(toastId);
            }
        });
    };

    const handleSaveOpstartplan = () => {
        putOpstartplan.mutate(
            { dossierId, treinseries: treinseries.map(Treinserie.toDto), toelichting },
            { onSuccess: () => onOpenChange(false) }
        );
    };

    const handleDeleteOpstartplan = () => {
        deleteOpstartplan.mutate(
            { dossierId },
            {
                onSuccess: () => {
                    setDeleteConfirmationDialogOpen(false);
                    onOpenChange(false);
                },
            }
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer
                className="md:min-w-[736px]"
                onInteractOutside={(e) => {
                    // Allow clicking on toasts without closing dialog
                    const target = e.target as HTMLElement;
                    if (target.closest("[data-sonner-toast]")) {
                        e.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>Opstartplan {opstartplan ? "aanpassen" : "toevoegen"}</DialogTitle>
                </DialogHeader>
                <DialogBody className="grid gap-4">
                    {!treinseries?.length ? (
                        <GeenTreinseries onAdd={() => setShowNewTrainForm(true)} />
                    ) : (
                        <>
                            <Treinseries treinseries={treinseries} onDelete={handleDeleteTrein} onEdit={handleEditTrein} actionsEnabled />
                            <Button
                                icon="plus"
                                iconStrokeWidth="thick"
                                size="s"
                                iconSize="xs"
                                className="justify-self-start"
                                variant="secondary"
                                onClick={() => setShowNewTrainForm(true)}
                            >
                                Trein toevoegen
                            </Button>
                        </>
                    )}
                    {showNewTrainForm && <TreinserieForm onCancel={() => setShowNewTrainForm(false)} onSubmit={handleAddTrein} />}
                    <Inputfield
                        label="Toelichting opstartplan"
                        optional
                        value={toelichting}
                        onValueChange={setToelichting}
                        helptext="Toelichtingen worden na toevoegen aan medewerkers in Wissel getoond"
                    />
                    {putOpstartplan.error && <ErrorDisplay error={putOpstartplan.error} />}
                </DialogBody>
                <DialogFooter>
                    {/* opstartplan verwijderen tijdelijk uitgezet, zie https://dev.azure.com/ns-topaas/RBP/_boards/board/t/Team%20COVER/Features?workitem=1037270 */}
                    {/* {opstartplan && (
                        <Button
                            type="button"
                            variant="tertiary"
                            destructive
                            className="mr-auto"
                            onClick={() => setDeleteConfirmationDialogOpen(true)}
                        >
                            Opstartplan verwijderen
                        </Button>
                    )} */}

                    <Button variant="tertiary" onClick={() => onOpenChange(false)}>
                        Annuleren
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveOpstartplan}
                        disabled={putOpstartplan.isPending || treinseries.length === 0}
                    >
                        {opstartplan ? "Opslaan" : "Toevoegen"}
                    </Button>
                </DialogFooter>
            </DialogContainer>
            <ConfirmDialog
                title="Opstartplan verwijderen"
                isOpen={deleteConfirmationDialogOpen}
                onConfirm={handleDeleteOpstartplan}
                confirmLabel="Verwijderen"
                isPending={deleteOpstartplan.isPending}
                error={deleteOpstartplan.error}
                onCancel={() => setDeleteConfirmationDialogOpen(false)}
                destructive
            >
                Weet je zeker dat je dit opstartplan wil verwijderen?
            </ConfirmDialog>
        </Dialog>
    );
}

type GeenTreinseriesProps = {
    onAdd: () => void;
};

function GeenTreinseries({ onAdd }: GeenTreinseriesProps) {
    return (
        <div className="grid justify-items-center gap-4">
            <Empty>
                <EmptyImage variant="tracks" />
                <EmptyHeader className="flex justify-center font-normal">
                    <EmptyDescription className="text-s text-content-subtle">
                        Voeg een trein toe, om een opstartplan te maken.
                    </EmptyDescription>
                </EmptyHeader>
            </Empty>
            <Button icon="plus" size="s" iconSize="xs" iconStrokeWidth="thick" onClick={onAdd}>
                Trein toevoegen
            </Button>
        </div>
    );
}
